<script>
    import { selectedItem, currentUser, 
             analyzeEnergyLoading, analyzeEnergyLoadingDone,
             energyUploadLoading, energyUploadLoadingDone 
           } from '../store/store.js';
    import { getFileName, fetchBrf } from '../utils/utils.js';
    import Logotype from './Logotype.svelte';
    import UploadFile from './UploadFile.svelte'; 
    import Login from './common/Login.svelte'; 
    import { get } from 'svelte/store';

    // Event handlers for the UploadFile component
    function handleEnergyUploadProgress(event) {
        const { stage, loading, done, error, type } = event.detail;
        if (type !== 'energy') return;

        if (stage === 'upload') {
            energyUploadLoading.set(loading);
            energyUploadLoadingDone.set(done && !error);
        } else if (stage === 'analysis') {
            analyzeEnergyLoading.set(loading);
            analyzeEnergyLoadingDone.set(done && !error);
        } else if (stage === 'expert') { // This is step 3 "Slutför bearbetning"
            // No specific global store for this step for energy, UploadFile handles its UI.
            // console.log('Energy processing step 3 (finalizing) progress:', { loading, done });
        }
        if (error) {
            console.error(`Error during ${stage} for energy:`, event.detail.message);
            // Reset relevant global stores on error if UploadFile doesn't cover it fully
            if (stage === 'upload') { energyUploadLoading.set(false); energyUploadLoadingDone.set(false); }
            if (stage === 'analysis') { analyzeEnergyLoading.set(false); analyzeEnergyLoadingDone.set(false); }
        }
    }

    function handleEnergyUploadComplete(event) {
        const { fileName, coreId, type } = event.detail;
        if (type !== 'energy') return;
        console.log(`Energy declaration upload and processing complete for ${fileName}, coreId: ${coreId}`);
        // $selectedItem is updated by fetchBrf called within UploadFile.
        // UI will reactively switch from UploadFile to the analysis/viewer section.
    }

    function handleEnergyUploadError(event) {
        const { message, type, stage } = event.detail;
        if (type !== 'energy') return;
        console.error(`Energy Upload Error from UploadFile component during ${stage}:`, message);
        // Reset all relevant global stores to ensure UI consistency
        energyUploadLoading.set(false);
        energyUploadLoadingDone.set(false);
        analyzeEnergyLoading.set(false);
        analyzeEnergyLoadingDone.set(false);
        // alert(`Upload failed: ${message}`); // Optional user feedback
    }

    // Renamed from original handleAnalyzeInitializedEnergy for clarity
    async function handleTriggerExistingEnergyAnalysis(){
        if (!$selectedItem?.energy_file_name || !$selectedItem?.core_id) {
            console.warn("Cannot trigger existing energy analysis: file name or core ID missing.");
            return;
        }
		try {
			analyzeEnergyLoadingDone.set(false);
			analyzeEnergyLoading.set(true);

            // This is the core logic from the original analyzeInitializedEnergy function
            let url = 'https://brf-analyze-backend-tnkskvn76a-lz.a.run.app/analyze_energy_gtp_4o_structured_initialized';
            const queryParams = new URLSearchParams({ energy_file_name: $selectedItem.energy_file_name });
            if ($selectedItem.core_id) {
                queryParams.append('core_id', $selectedItem.core_id);
            }
            const storeState = get(currentUser); // Helper to get current value
            const response = await fetch(`${url}?${queryParams.toString()}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${storeState?.accessToken}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Failed to trigger backend function for existing energy. Status: ${response.status}, Message: ${errorText}`);
                throw new Error('Failed to trigger backend function for existing energy declaration');
            }

            const data = await response.json();
            console.log('Backend function response for existing energy:', data);
            analyzeEnergyLoadingDone.set(true);
			fetchBrf($selectedItem.core_id); 
		} catch (error) {
			console.error('Error in re-analyzing energy process:', error.message);
            analyzeEnergyLoadingDone.set(false);
		} finally {
			analyzeEnergyLoading.set(false);
		}
	}
</script>

{#if !$currentUser?.accessToken && !$selectedItem.energy_file_name}
<div class="key-value-table solo-table" style="max-width: 310px; margin-bottom: 40px;">
  <h3>Boanalys har inte tillgång till energideklarationen</h3>
  <p>Du behöver logga in för att ladda upp energideklarationen.</p>
</div>
<Login />
{:else if !$selectedItem.energy_file_name}
  <!-- Section for uploading energy declaration if not available -->
  <div style="margin-top: 0px !important;">
    <UploadFile
        uploadType="energy"
        associatedCoreId={$selectedItem.core_id}
        textsConfig={{ 
            title: `Ladda upp Energideklaration för ${$selectedItem.name || 'denna förening'}`,
            buttonText: 'Välj Energideklaration (PDF)',
            infoText: 'Ladda upp energideklarationen för att möjliggöra analys.'
        }}
        on:progress={handleEnergyUploadProgress}
        on:complete={handleEnergyUploadComplete}
        on:error={handleEnergyUploadError}
    />
  </div>

{:else if $selectedItem.energy_file_name}
  <!-- Section for initial analysis state (if file exists but not analyzed, e.g. energy_declaration_id is null) -->
  {#if $selectedItem.energy_declaration_id === null}
    <div class="analyze-about-text" style="margin-top: 0px !important;">
      {#if !$analyzeEnergyLoading} 
        <div class="ready-state centered-layout">
            <div class="content-container">
                <h5>Analysera energideklarationen för djupare insikter!</h5>
                <p class="description">
                    Energideklarationen finns tillgänglig men är inte analyserad än. Starta analysen för att:
                </p>
                <ul class="benefits-list">
                    <li>Förstå föreningens energiförbrukning.</li>
                    <li>Identifiera eventuella avvikelser från standard.</li>
                </ul>
				<p></p>
                <button
                    type="button"
                    class="action-button"
                    on:click={handleTriggerExistingEnergyAnalysis}
                >
                    Starta Analys <span class="button-arrow">&rarr;</span>
                </button>
            </div>
        </div>
      {:else}
        <!-- Show Analysis Loading Card only -->
        <div class="loading-container">
            <h3 class="loading-header">Analyserar energideklaration...</h3>
            <div class="loading-items-wrapper">
                <div class="loading-item">
					<div class="question">
						<Logotype scale={0.6}></Logotype> 
					</div>
                    <div class="loading-text"> <span>Analyserar energideklarationen (ca 20 sek)...</span> </div>
                </div>
            </div>
        </div>
      {/if} 
    </div>
  {/if} 
  
  <!-- PDF Viewer (show if file exists, analysis is done, and user has access) -->
  {#if ($currentUser?.subscription_tier === 'Pro' || $currentUser?.subscription_tier === 'Plus') && $selectedItem.energy_declaration_id !== null}
    <div class="pdf-container" style="margin-top: 20px;">
      <iframe class="iframe" title="Energideklaration" src={ getFileName($selectedItem?.energy_file_name) } frameborder=0 ></iframe>
    </div>
  {/if}
{/if} 

<style>
    p.description {
        font-size: 0.95rem;
        color: #495057;
        line-height: 1.6;
        margin-bottom: 15px;
        text-align: center;
    }
    .auth-message p {
        font-size: 1rem;
        color: #333;
        margin-bottom: 10px;
    }
</style> 